import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  loadManagedProduct,
  updateManagedProduct,
  linkCapability,
  unlinkCapability,
  linkCapabilities,
  unlinkCapabilities,
} from 'js/actions/managed-product-actions';
import { listCapabilitySets } from 'js/actions/capability-actions';
import { useQuery } from 'js/hooks/useQuery';
import { useStandardCollator } from 'js/utils/string';

import { default as ManagedProductComponent } from '../components/ManagedProduct';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { denormalize } from 'normalizr';
import { useNotify } from 'js/hooks/useNotify';
import { SCHEMAS } from 'js/api/schemas';

function ManagedProduct({
  managedProduct,
  loadManagedProduct,
  updateManagedProduct,
  listCapabilitySets,
  linkCapability,
  unlinkCapability,
  linkCapabilities,
  unlinkCapabilities,
}) {
  const { productId, workspaceId } = useParams();
  const notify = useNotify();
  const [hasLoaded, setHasLoaded] = useState(managedProduct?.stats != null);

  const refreshCapabilitySetsQuery = useCallback(
    (queryId) => listCapabilitySets(workspaceId, queryId),
    [workspaceId, listCapabilitySets]
  );

  const { refresh: refreshCapabilitySets, result: capabilitySets } = useQuery({
    queryId: `${workspaceId}-capability-sets`,
    query: refreshCapabilitySetsQuery,
    schema: SCHEMAS.CAPABILITY_SET_ARRAY,
  });

  const refreshManagedProduct = useCallback(
    () => loadManagedProduct(productId),
    [loadManagedProduct, productId]
  );

  const collator = useStandardCollator();
  const sortedCapabilitySets = useMemo(() => {
    return capabilitySets?.sort(
      (a, b) => collator.compare(a.name, b.name) || collator.compare(a.id, b.id)
    );
  }, [capabilitySets, collator]);

  useEffect(() => {
    async function refresh() {
      await refreshManagedProduct();
      setHasLoaded(true);
    }

    refresh();
  }, [refreshManagedProduct]);

  useEffect(() => {
    refreshCapabilitySets();
  }, [refreshCapabilitySets]);

  const handleUpdate = useCallback(
    async (values) => {
      return updateManagedProduct({ id: productId, ...values })
        .then(() => {
          notify({
            type: 'success',
            message: 'Product manager updated successfully',
          });
          return refreshManagedProduct();
        })
        .catch((error) => {
          notify({
            type: 'danger',
            message: 'Failed to update product manager',
          });
          throw error;
        });
    },
    [updateManagedProduct, productId, notify, refreshManagedProduct]
  );

  const handleLinkCapability = useCallback(
    async (capability) => {
      return linkCapability(productId, capability.id).then(
        refreshManagedProduct
      );
    },
    [linkCapability, productId, refreshManagedProduct]
  );

  const handleUnlinkCapability = useCallback(
    async (capability) => {
      return unlinkCapability(productId, capability.id).then(
        refreshManagedProduct
      );
    },
    [unlinkCapability, productId, refreshManagedProduct]
  );

  const handleLinkCapabilities = useCallback(
    async (capabilities) => {
      return linkCapabilities(
        productId,
        capabilities.map((c) => c.id)
      ).then(refreshManagedProduct);
    },
    [linkCapabilities, productId, refreshManagedProduct]
  );

  const handleUnlinkCapabilities = useCallback(
    async (capabilities) => {
      return unlinkCapabilities(
        productId,
        capabilities.map((c) => c.id)
      ).then(refreshManagedProduct);
    },
    [unlinkCapabilities, productId, refreshManagedProduct]
  );

  return (
    <ManagedProductComponent
      managedProduct={managedProduct}
      capabilitySets={sortedCapabilitySets}
      onUpdate={handleUpdate}
      hasLoaded={hasLoaded}
      onLinkCapability={handleLinkCapability}
      onUnlinkCapability={handleUnlinkCapability}
      onLinkCapabilities={handleLinkCapabilities}
      onUnlinkCapabilities={handleUnlinkCapabilities}
    />
  );
}

export default connect(
  (state, { match }) => ({
    managedProduct: denormalize(
      state.entities.managedProducts[match.params.productId],
      SCHEMAS.MANAGED_PRODUCT,
      state.entities
    ),
  }),
  {
    loadManagedProduct,
    updateManagedProduct,
    listCapabilitySets,
    linkCapability,
    unlinkCapability,
    linkCapabilities,
    unlinkCapabilities,
  }
)(ManagedProduct);
